import { ReviewBox } from './ReviewBox/ReviewBox';
import { LabelText, ValueText } from '../DetailView.styled';
import {
  LabelAddress,
  ReviewTimestampText,
  SeparatingLine,
  TooltipWrapper,
  CallOutWrapper,
} from './GeneralTab.styled';
import { Flex } from 'components/Flex';
import {
  OrderCancelCategoryEnum,
  Order,
  OrderStatusEnum,
  Location as GraphQLLocation,
} from 'types/graphql';
import { FC, useCallback, useState } from 'react';
import { RateShiftViewProvider } from 'modals/ShiftModal/src/views/RateShiftView/RateShiftView.context';
import { StarRating } from 'components/StarRating';
import { parseDate } from 'utils';
import { css } from 'styled-components';
import { UserGuard } from 'guards/UserGuard';
import { IGeneralTabProps } from 'modals/ShiftModal/src/views/DetailView/GeneralTab/GeneralTab.types';
import { formatTime, formatUnpaidBreakTime } from '../TimesheetsTab/TimesheetsTab.utils';
import { RecurrenceList } from 'modals/ShiftModal/components/RecurrenceList';
import { BKJButton, BKJIcon, BKJIconTooltip } from '@bookjane2/bookjane-design-library';
import { formatLocation, getShiftModalShiftJaneName } from 'mappers/shiftModalOrderDetailsMapper';
import { MountTransition } from 'routes';
import { SectionDivider } from 'modals/ShiftModal/ShiftModal.styled';
import { ShiftCustomFields } from 'modals/ShiftModal/src/views/DetailView/GeneralTab/ShiftCustomFields/ShiftCustomFields';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { useSelector } from 'react-redux';
import { BKJInlineGoogleMapsComponent } from 'modals/ShiftModal/components/BKJInlineGoogleMapView';
import {
  BKJGoogleMapsProvider,
  createGoogleMapsUrl,
} from 'modals/ShiftModal/components/BKJGoogleMapsProvider';
import {
  CallOutSection,
  CalloutNotFinalizedView,
} from 'modals/ShiftModal/src/views/DetailView/GeneralTab/CallOut';
import { AnchorAppLink } from 'components/AnchorAppLink/AnchorAppLink';
import { useTranslation } from 'hooks/useTranslation';
import { isAuditTrailFeatureEnabledForCommunitySelector } from 'store/selectors/featureFlagSelectors';
import { DetailViewInnerHeader } from 'modals/ShiftModal/src/views/DetailView/DetailTabView/DetailTabView';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import {
  BiddingSection,
  IBiddingSectionProps,
} from 'modals/ShiftModal/src/views/DetailView/GeneralTab/Bidding';
import { getShiftBiddingStatus } from 'utils/shiftStatus';
import { formatPhoneNumber } from 'utils/formatPhoneNumber';
import { UserPermissionEnum } from 'guards/UserGuard.types';

import { FormatCurrency } from 'components/FormatCurrency';

import { startCase } from 'utils/startCase';
import { useBuildActionsForShiftCard } from 'components/RenderShiftActions/RenderShiftActions.constants';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { RenderShiftActionsProps } from 'components/RenderShiftActions/RenderShiftActions.types';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { useIsAgencyShift } from 'hooks/useAgencyHooks';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import {
  ShiftBlockOrdersDataProvider,
  ShiftBlockOrdersList,
  useShiftBlockOrdersDataContext,
} from 'modals/ShiftModal/components/ShiftBlockOrdersList';

const ShiftBlockOrdersListConnected: FC<{ isAwarded?: boolean }> = ({ isAwarded }) => {
  const { orders, isLoading, hasNextPage, loadMore } = useShiftBlockOrdersDataContext();
  return (
    <ShiftBlockOrdersList
      orders={orders}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      loadMore={loadMore}
      isAwarded={isAwarded}
    />
  );
};

export const shiftModalGeneralTabValueTextMarginLeft = 16;
export const shiftModalGeneralTabFormItemVerticalMargin = 24;
const formatRepeatDays = (weekdays: string[]) => weekdays.map((day) => startCase(day)).join(', ');
const ShiftActionComponent: RenderShiftActionsProps['SingleItemComponent'] = ({
  onClick,
  children,
}) => (
  <BKJButton variant="PurpleSolid" width="180px" padding="0" onClick={onClick}>
    {children}
  </BKJButton>
);

const CallOutView: FC = () => {
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const hasCallout =
    order?.status?.value === OrderStatusEnum.pending && order?.currentlyActiveCalloutList;
  const isCalloutBeingFinalized =
    !order?.currentlyActiveCalloutList &&
    !!order?.draftShift?.id &&
    order.status.value === OrderStatusEnum.pending;

  const showCallout = hasCallout && !isCalloutBeingFinalized;
  const showCalloutBeingFinalized = !showCallout && isCalloutBeingFinalized;

  return (
    <Flex>
      {showCallout && (
        <CallOutWrapper>
          <Flex flexDirection="column" flex="1">
            <CallOutSection />
          </Flex>
        </CallOutWrapper>
      )}
      {showCalloutBeingFinalized && (
        <CallOutWrapper>
          <Flex flexDirection="column" flex="1">
            <CalloutNotFinalizedView />
          </Flex>
        </CallOutWrapper>
      )}
    </Flex>
  );
};

export const GeneralTab: FC<IGeneralTabProps> = ({ data }): JSX.Element => {
  const order = data?.order;
  const { t, i18n } = useTranslation();
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);
  const format = `dd, yyyy '${t('shift_modal:AT')}' ${is24hTimeFormat ? 'HH:mm' : 'hh:mm a'}`;
  const isUserTypeAgency = useSelector(isUserAgencySelector);
  const { hasMarketplace, value } = getCurrentlySelectedLocation();

  const isAgencyShift = useIsAgencyShift({
    mainJane: order!.mainJane,
    orderCommunityOrgId: order!.community?.organization?.id,
    sentToAgencies: order!.sentToAgencies,
  });

  const isAgencyOrExternalJane = (isAgencyShift || order!.isExternal) && !!order!.mainJane;

  const communityForAgencyInfo = isAgencyShift
    ? order!.mainJane?.agencyCommunity || order!.mainJane?.primaryCommunity
    : null;

  const isCompletedByAgency =
    isAgencyOrExternalJane && order!.status.value === OrderStatusEnum.completed;

  const [isContinueCallOutConfirmationOpen, setIsContinueCallOutConfirmationOpen] = useState(false);
  const [isAllowOvertimeConfirmationOpen, setIsAllowOvertimeConfirmationOpen] = useState(false);
  const [isAutoRebidConfirmationOpen, setIsAutoRebidConfirmationOpen] = useState(false);
  const [isBlockDeleteConfirmationOpen, setIsBlockDeleteConfirmationOpen] = useState(false);
  const { goToSendToAgenciesView } = useShiftModalPropsContext();
  const openSendToAgenciesFlow = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      goToSendToAgenciesView();
    },
    [goToSendToAgenciesView],
  );
  const goToAwardTeamMemberFlow = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      shiftModalRoutingService.open(
        { shiftModalShiftId: order!.relayId ?? '' },
        'AwardTeamMemberView',
      );
    },
    [order!.relayId],
  );
  const { shiftActions, isShiftInfoWrapperVisible } = useBuildActionsForShiftCard({
    order: data?.order,
    setIsContinueCallOutConfirmationOpen,
    setIsAllowOvertimeConfirmationOpen,
    setIsAutoRebidConfirmationOpen,
    setIsBlockDeleteConfirmationOpen,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    goToAwardTeamMemberFlow: (e: any) => {
      setTimeout(() => {
        goToAwardTeamMemberFlow(e);
      }, 250);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openSendToAgenciesFlow: (e: any) =>
      setTimeout(() => {
        return openSendToAgenciesFlow(e);
      }, 250),
  });
  const biddingSectionProps: IBiddingSectionProps = {
    shiftActions,
    isShiftInfoWrapperVisible,
    ShiftActionComponent,
    isContinueCallOutConfirmationOpen,
    isAllowOvertimeConfirmationOpen,
    setIsContinueCallOutConfirmationOpen,
    setIsAllowOvertimeConfirmationOpen,
    isAutoRebidConfirmationOpen,
    setIsAutoRebidConfirmationOpen,
    isBlockDeleteConfirmationOpen,
    setIsBlockDeleteConfirmationOpen,
  };

  const showRate = isCompletedByAgency || order!.status.value !== OrderStatusEnum.completed;

  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) && !order!.isExternal;

  const isAuditTrailEnabled = useSelector(
    isAuditTrailFeatureEnabledForCommunitySelector({
      communityId: hasMarketplace ? Number(value) : order!.community.id,
      auditTrailVersion: order!.auditTrailVersion,
    }),
  );

  const cancellationReasonCategory = order!.cancellationReason?.reasonCategory;
  const cancellationReasonMessage = order!.cancellationReason?.reasonMessage;
  const isCancellationReasonOther = cancellationReasonCategory === OrderCancelCategoryEnum.other;
  const translateCancellationReason = isCancellationReasonOther
    ? cancellationReasonMessage ?? ''
    : getTranslatedResponse(cancellationReasonCategory ?? '', 'status', i18n, t);

  const biddingStatus = getShiftBiddingStatus({
    status: order!.status.value,
    biddable: order!.biddable,
    biddingTimeEndsAt: order!.biddingTimeEndsAt,
    awardingTimeStartsAt: order!.awardingTimeStartsAt,
    bids: order!.bids,
  });

  const isBiddingShift =
    !!order!.biddable &&
    (biddingStatus === 'BIDDING' || biddingStatus === 'AWARDING' || biddingStatus === 'NO_BIDS');

  const isShowBiddingView = isBiddingShift && !isAgencyShift && !order!.acceptedBySharedTeamMember;

  const isAgencyUser = isUserTypeAgency || hasMarketplace;

  const isShowMap =
    order!.location?.longitude &&
    order.location.latitude &&
    order.location.fullAddress &&
    (order.isExternal || isUserTypeAgency);
  return (
    <Flex flexDirection="column" childMarginBottom="40px" flex="1">
      <MountTransition>
        <DetailViewInnerHeader data={data} />
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
            UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
          ]}
        >
          {isShowBiddingView ? (
            <BiddingSection {...biddingSectionProps} />
          ) : (
            isAuditTrailEnabled && !order!.isExternal && <CallOutView />
          )}
        </UserGuard>
        <Flex>
          {order!.isReviewAllowed &&
          !order!.noShow &&
          // Check if mainJane is present as a completed shift may not be assigned
          order!.mainJane ? (
            <UserGuard
              userType={[
                'USER_TYPE_B2B',
                'USER_TYPE_PREMIUM_SECONDARY',
                'USER_TYPE_ESSENTIAL_SECONDARY',
              ]}
            >
              <Flex flexDirection="column" width="100%" padding="32px 32px 0">
                <RateShiftViewProvider>
                  <ReviewBox janeName={getShiftModalShiftJaneName(data?.order ?? null) as string} />
                </RateShiftViewProvider>
              </Flex>
            </UserGuard>
          ) : (
            order!.review && (
              <Flex
                flexDirection="column"
                width="100%"
                padding="32px 32px 40px"
                css={css`
                  border-bottom: 1px solid #d3d7dc;
                `}
              >
                <Flex justifyContent="space-between">
                  <LabelText>{t('shift_modal:SUBMITTED_REVIEW')}</LabelText>
                  <ReviewTimestampText>
                    {parseDate(order!.review.updatedAt, format)}
                  </ReviewTimestampText>
                </Flex>
                <Flex padding="12px 0">
                  <StarRating rating={order!.review.rating as number} />
                </Flex>
                <LabelText>{order!.review.comment}</LabelText>
              </Flex>
            )
          )}
        </Flex>

        <Flex flexDirection="column" padding="32px" childMarginBottom="32px">
          <Flex>
            <Flex flexDirection="column" width="240px">
              <LabelText>{t('shift_modal:TOTAL_PAID_DURATION')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {order!.noShow ? '0 hrs' : formatTime(order!.totalDuration ?? 0)}
              </ValueText>
            </Flex>

            {order!.orderSchedule.breakTime && (
              <Flex flexDirection="column">
                <LabelText>{t('shift_modal:UNPAID_BREAK_TIME')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {formatUnpaidBreakTime(order!.orderSchedule.breakTime)}
                </ValueText>
              </Flex>
            )}
          </Flex>
          {order!.recurrence && (
            <>
              <Flex>
                {order!.recurrence.frequency && (
                  <Flex flexDirection="column" width="240px">
                    <LabelText>{t('shift_modal:FREQUENCY')}</LabelText>
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                      {order!.recurrence.recurrenceLabel}
                    </ValueText>
                  </Flex>
                )}
                {order!.recurrence.expirationDate && (
                  <Flex flexDirection="column">
                    <LabelText>{t('shift_modal:ENDS_ON')}</LabelText>
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                      {parseDate(order!.recurrence.expirationDate)}
                    </ValueText>
                  </Flex>
                )}
              </Flex>
              {order!.recurrence.weekdays && order!.recurrence.weekdays.length > 0 && (
                <Flex>
                  <Flex flexDirection="column">
                    <LabelText>{t('shift_modal:REPEAT_DAYS')}</LabelText>
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                      {formatRepeatDays(order!.recurrence.weekdays)}
                    </ValueText>
                  </Flex>
                </Flex>
              )}
            </>
          )}
          {order!.subFacilityAddress?.name && !isAgencyUser ? (
            <Flex flexDirection="column" gap="8px">
              <LabelText>Customer</LabelText>
              <Flex flexDirection="column" gap="4px">
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {order!.subFacilityAddress.name}
                </ValueText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {order!.subFacilityAddress.address}
                </ValueText>
              </Flex>
            </Flex>
          ) : (
            order!.location && (
              <Flex flexDirection="column">
                <LabelText>
                  {isAgencyUser ? t('shift_modal:CUSTOMER') : t('shift_modal:LOCATION')}
                </LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {order!.community.name}
                </ValueText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {order!.location.fullAddress && (
                    <Flex>
                      <Flex flexDirection="column" width="25px">
                        <BKJIcon iconName="Location" color="AccentPurple" height="19px" />
                      </Flex>
                      <Flex flexDirection="column">
                        <a
                          href={createGoogleMapsUrl(order!.location.fullAddress)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <LabelAddress>
                            {formatLocation(order.location as GraphQLLocation)}
                          </LabelAddress>
                        </a>
                      </Flex>
                    </Flex>
                  )}
                </ValueText>
                {isShowMap && (
                  <BKJGoogleMapsProvider>
                    <BKJInlineGoogleMapsComponent
                      height={'132px'}
                      width={'500px'}
                      addressInfo={{
                        longitude: Number(order.location.longitude),
                        latitude: Number(order.location.latitude),
                        fullAddress: String(order.location.fullAddress),
                      }}
                      zoom={13}
                    />
                  </BKJGoogleMapsProvider>
                )}
              </Flex>
            )
          )}
          {showRate &&
            order!.rate &&
            order!.rate.rateLabel !== '-' &&
            order!.estimatedTotalCharge &&
            order!.estimatedTotalCharge.rateLabel !== '-' &&
            order!.orderSchedule.timeSheet &&
            order!.orderSchedule.timeSheet.status && (
              <Flex>
                <Flex flexDirection="column" width="240px">
                  <Flex>
                    <LabelText>
                      {isAgencyUser
                        ? t('shift_modal:HOURLY_CHARGE_RATE')
                        : t('shift_modal:ESTIMATED_HOURLY_COST')}
                      &nbsp;&nbsp;
                    </LabelText>
                    <BKJIconTooltip placement="bottom" iconName="Info">
                      <TooltipWrapper>{t('GENERAL_TAB_TOOLTIP_TEXT')}</TooltipWrapper>
                    </BKJIconTooltip>
                  </Flex>
                  <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                    <FormatCurrency amount={`${order!.rate.rateLabel}`} />
                  </ValueText>
                </Flex>
                <Flex flexDirection="column">
                  <LabelText>{t('shift_modal:ESTIMATED_TOTAL_COST')}</LabelText>
                  <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                    <FormatCurrency amount={`${order!.estimatedTotalCharge.rateLabel}`} />
                  </ValueText>
                </Flex>
              </Flex>
            )}
          {!order.isExternal && (
            <Flex flexDirection="column">
              <LabelText>{t('shift_modal:OVERTIME')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {order!.allowOvertime ? t('shift_modal:ALLOWED') : t('shift_modal:NOT_ALLOWED')}
              </ValueText>
            </Flex>
          )}
          {order!.description && (
            <Flex flexDirection="column">
              <LabelText>{t('shift_modal:SHIFT_NOTES')}</LabelText>
              {order!.description.split('\n').map((textSegment, i, { length }) => (
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {textSegment}
                  {/** Add line break if not first or last text segment */}
                  {i > 0 && i < length - 1 && <br />}
                </ValueText>
              ))}
            </Flex>
          )}
          {communityForAgencyInfo && !order!.isExternal && (
            <Flex>
              <Flex flexDirection="column" width="240px" gap="3px">
                <LabelText>{t('shift_modal:AGENCY_NAME')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {communityForAgencyInfo.name}
                </ValueText>
              </Flex>
              <Flex flexDirection="column" gap="3px">
                <LabelText>{t('shift_modal:AGENCY_PHONE_NUMBER')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  <AnchorAppLink
                    href={`tel:${communityForAgencyInfo.contactPhone}`}
                    css={css`
                      text-decoration: underline;
                    `}
                  >
                    {formatPhoneNumber(String(communityForAgencyInfo.contactPhone))}
                  </AnchorAppLink>
                </ValueText>
              </Flex>
            </Flex>
          )}

          {order!.status.value === OrderStatusEnum.cancelled && (
            <Flex flexDirection="column">
              <LabelText>{t('shift_modal:CANCELLATION_REASON')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {translateCancellationReason}
              </ValueText>
            </Flex>
          )}
        </Flex>
        {isCustomFieldsEnabled && (
          <>
            <SectionDivider
              variant="Thin"
              margin={`0 0 ${shiftModalGeneralTabFormItemVerticalMargin}px 0`}
            />
            <Flex flexDirection="column" padding="0 32px 32px 32px" childMarginBottom="32px">
              <ShiftCustomFields order={data?.order} />
            </Flex>
          </>
        )}

        {order!.futureShifts.length > 0 && (
          <>
            <SeparatingLine />
            <Flex flexDirection="column" padding="32px">
              <RecurrenceList shifts={order!.futureShifts as Order[]} />
            </Flex>
          </>
        )}
        {order.isBlockShift && (
          <Flex flexDirection="column" padding="0 32px 32px">
            <ShiftBlockOrdersDataProvider orderRelayId={order.relayId} excludeOrderId={order.id}>
              <ShiftBlockOrdersListConnected
                isAwarded={order.status.value === OrderStatusEnum.accepted}
              />
            </ShiftBlockOrdersDataProvider>
          </Flex>
        )}
      </MountTransition>
    </Flex>
  );
};
